import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, InjectionToken, Injector, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CustomTranslateHttpLoader } from './core/common/custom-translate-http-loader';
import { GlobalErrorHandler } from './core/common/global-error-handler';
import { JwtInterceptor } from './core/interceptors/jwt.interceptor';
import { LoaderInterceptor } from './core/interceptors/loader.interceptor';
import { API_BASE_URL } from './core/services/api-services';
import { AppConfigService } from './core/services/app-config.service';
import { LanguageService } from './core/services/language.service';
import { SharedModule } from './shared/shared.module';
import localeDECH from '@angular/common/locales/de-CH';
import localeENCH from '@angular/common/locales/en-CH';
import localeFRCH from '@angular/common/locales/fr-CH';
import localeITCH from '@angular/common/locales/it-CH';
import { HttpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateMatPaginatorIntl } from './core/common/translate-mat-paginator-intl';
import { LanguageInterceptor } from './core/interceptors/language.interceptor';
import { LocaleProvider } from './core/providers/locale.provider';
import { switchMap } from 'rxjs';
import DateTimeFormats from './core/constants/date-time-formats';

registerLocaleData(localeDECH);
registerLocaleData(localeENCH);
registerLocaleData(localeFRCH);
registerLocaleData(localeITCH);

const initFactory = (
  appConfigService: AppConfigService,
  injector: Injector
) => {
  // Initialize the AppConfig first because services depend on it.
  return () => appConfigService.init()
    .pipe(switchMap(() => injector.get(LanguageService).init()));
};

function getBaseUrl(appConfigService: AppConfigService): string {
  return appConfigService.getConfig().apiUrl;
}

function customTranslateHttpLoaderFactory(injector: Injector) {
  return new CustomTranslateHttpLoader(injector);
}

export const WINDOW = new InjectionToken<Window>('window');

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    SharedModule,
    HttpClientModule,
    MatIconModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: customTranslateHttpLoaderFactory,
        deps: [Injector],
      },
    }),
    RouterModule
  ],
  exports: [SharedModule],
  providers: [
    { provide: APP_INITIALIZER, useFactory: initFactory, deps: [AppConfigService, Injector], multi: true },
    { provide: API_BASE_URL, useFactory: getBaseUrl, deps: [AppConfigService] },
    LocaleProvider,
    LanguageService,
    {
      provide: MatPaginatorIntl,
      useClass: TranslateMatPaginatorIntl
    },
    { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: WINDOW,
      useValue: window
    },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: DateTimeFormats.shortDate } }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
